import React, { useEffect, useState } from 'react';
import { useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { StatusDot } from '@xsolla/ui-core';

import { VerticalCenterBlock } from '../../../styled';
import { AlertIcon, FieldBlock, Separator, ToggleBoxInForm } from '../../ui';
import { FieldInputTitle } from '../../ui/FieldInput/style';
import TestValidation from './TestValidation';
import { ExtendedProjectConfiguration, Pallets, Props } from './types';
import { FieldValidationMap, protectStatuses } from './utils';
import ValidationFields from './ValidationFields';

const ValidationRule = ({ name }: Props): JSX.Element | null => {
  const intl = useIntl();
  const { hasProtectStatus } = FieldValidationMap[name];
  const [protectStatus, setProtectStatus] = useState<protectStatuses>(protectStatuses.ABSENT);
  const [statusBrand, setStatusBrand] = useState<Pallets>('danger');
  const { values } = useFormState<ExtendedProjectConfiguration>();

  useEffect(() => {
    if (!hasProtectStatus || !values?.fields_validation) {
      return;
    }
    const numberOfRules = Object.values(values.fields_validation).filter((value) => !!value).length;

    if (!numberOfRules) {
      setProtectStatus(protectStatuses.ABSENT);
    } else if (numberOfRules === 5) {
      setProtectStatus(protectStatuses.STRONG);
    } else if (numberOfRules >= 3 && numberOfRules <= 4) {
      setProtectStatus(protectStatuses.GOOD);
    } else if (numberOfRules >= 1 && numberOfRules <= 2) {
      setProtectStatus(protectStatuses.WEAK);
    }
  }, [values]);

  useEffect(() => {
    switch (protectStatus) {
      case protectStatuses.ABSENT:
        setStatusBrand('danger');
        break;
      case protectStatuses.WEAK:
        setStatusBrand('danger');
        break;
      case protectStatuses.GOOD:
        setStatusBrand('warning');
        break;
      case protectStatuses.STRONG:
        setStatusBrand('action');
        break;
      default:
        setStatusBrand('danger');
        break;
    }
  }, [protectStatus]);

  return (
    <ToggleBoxInForm
      title={intl.formatMessage({
        id: `configuration-page.${name}.${name}-validation.toggle-box.title`
      })}
      description={intl.formatMessage({
        id: `configuration-page.${name}.${name}-validation.toggle-box.description`
      })}
      statusType={hasProtectStatus ? protectStatus : null}
      status={
        hasProtectStatus && (
          <StatusDot palette={statusBrand}>
            {intl.formatMessage({ id: `configuration-page.password.status.${protectStatus}` })}
          </StatusDot>
        )
      }
      openByDefault>
      <ValidationFields name={name} />

      <Separator margin="28px 0" />

      <FieldBlock
        dataTestId="password-validation__test-validation"
        contentMargin="24px"
        title={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <VerticalCenterBlock>
            <AlertIcon />
            {intl.formatMessage({
              id: `configuration-page.${name}.${name}-validation.tests.title`
            })}
          </VerticalCenterBlock>
        }
        description={intl.formatMessage({
          id: `configuration-page.${name}.${name}-validation.tests.description`
        })}>
        <FieldInputTitle>
          {intl.formatMessage({
            id: `configuration-page.${name}.${name}-validation.tests.input-title`
          })}
        </FieldInputTitle>

        <TestValidation name={name} />
      </FieldBlock>
    </ToggleBoxInForm>
  );
};

export default ValidationRule;
